import React, { useEffect, useState } from 'react';
import { bool, oneOf, number, string } from 'prop-types';
import { VelocityComponent } from 'velocity-react';
import IMG_UNAVAILABLE from '~IMAGES/image_unavailable.png';

import { http } from '../../util';
import { formatValues, imageSetValues, placeholderValue } from './propTypeValidations';
import { Zoom } from './Zoom';

const PREFIX = 'aurora_';
window.aurora = {};

const ProductImage = ({
  alt,
  backgroundRgba,
  format,
  greyscale,
  height,
  imageSet,
  placeholder,
  productCode,
  quality,
  viewCode,
  width,
  magnify
}) => {
  const cacheKey = `${PREFIX}${productCode}${viewCode || imageSet}`;
  const [imageSource, setImageSource] = useState(window.aurora[cacheKey] || '');
  const [showImage, setShowImage] = useState(typeof window.aurora[cacheKey] !== 'undefined' || false);
  const [selected, setSelected] = useState(false);

  const cacheImage = imageUrl => {
    setImageSource(imageUrl);
    window.aurora[cacheKey] = imageUrl;
  };

  const requestImage = async productCode => {
    try {
      const response = await http.get(
        import.meta.env.VITE_PRODUCT_IMAGE_URL.concat(
          `?productCode=${productCode}`,
          viewCode ? `&viewCode=${viewCode}` : `&imageSet=${imageSet}`,
          `&width=${magnify ? 250 : width}`,
          height ? `&height=${magnify ? 250 : height}` : '',
          `&format=${format}`,
          `&quality=${quality}`,
          `&backgroundRgba=${backgroundRgba}`,
          `&greyscale=${greyscale}`,
          `&placeholder=${placeholder}`
        )
      );
      const imageUrl = response instanceof Blob ? URL.createObjectURL(response) : response.url;

      cacheImage(imageUrl);
    } catch {
      cacheImage(IMG_UNAVAILABLE);
    }
  };

  useEffect(() => {
    setImageSource(window.aurora[cacheKey] || '');
  }, [cacheKey]);

  useEffect(() => {
    if (!imageSource) requestImage(productCode);
  }, [imageSource]);

  const select = () => setSelected(true);
  const deselect = () => setSelected(false);

  const Image = (
    <img
      data-testid='_test_image'
      alt={alt}
      className='productImage'
      onLoad={() => setShowImage(true)}
      src={imageSource}
    />
  );

  return (
    <VelocityComponent animation={showImage ? 'fadeIn' : 'fadeOut'}>
      {magnify ? (
        <Zoom
          selected={selected}
          onFocus={select}
          onBlur={deselect}
          onMouseOver={select}
          onMouseOut={deselect}
          style={{ width, height }}
        >
          {Image}
        </Zoom>
      ) : (
        Image
      )}
    </VelocityComponent>
  );
};

ProductImage.defaultProps = {
  backgroundRgba: '0,0,0,0',
  format: 'png',
  greyscale: false,
  height: null,
  imageSet: 'PV',
  magnify: false,
  placeholder: 'spil',
  quality: 80,
  viewCode: null,
  width: 800
};

const isBetweenNumericRange = (props, propName) => {
  const value = props[propName];

  if (typeof value !== 'number' || value < 0 || value > 100) {
    return new Error(`Invalid prop. ${propName} must be a number between 0 and 100.`);
  }

  return null;
};

ProductImage.propTypes = {
  alt: string.isRequired,
  backgroundRgba: string,
  format: oneOf(formatValues),
  greyscale: bool,
  height: number,
  imageSet: oneOf(imageSetValues),
  magnify: bool,
  placeholder: oneOf(placeholderValue),
  productCode: string.isRequired,
  quality: isBetweenNumericRange,
  viewCode: string,
  width: number
};

export default ProductImage;
